import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { 
  Users, 
  Printer, 
  Car, 
  GraduationCap, 
  CheckCircle2,
  Loader2,
  ArrowRight,
  Briefcase,
  Shield,
  Clock,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getCategories, getSettings } from '@/lib/api';

const iconMap = {
  Users: Users,
  Printer: Printer,
  Car: Car,
  GraduationCap: GraduationCap,
};

const slugMap = {
  'İnsan Kaynakları Hizmetleri': 'insan-kaynaklari',
  'Dijital Baskı Çözümleri': 'dijital-baski',
  'Araç Kiralama Hizmetleri': 'arac-kiralama',
  'Eğitim ve Danışmanlık': 'egitim-danismanlik',
};

// Image Carousel Component
const ImageCarousel = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [images.length]);

  const goToNext = () => setCurrentIndex((prev) => (prev + 1) % images.length);
  const goToPrev = () => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);

  return (
    <div className="relative w-full h-full overflow-hidden group">
      {images.map((img, idx) => (
        <img
          key={idx}
          src={img}
          alt={`${title} ${idx + 1}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
            idx === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}
      {images.length > 1 && (
        <>
          <button
            onClick={goToPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentIndex ? 'bg-white w-4' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const ServicesPage = () => {
  const [categories, setCategories] = useState([]);
  const [heroSettings, setHeroSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, heroRes] = await Promise.all([
          getCategories(),
          getSettings('services_hero')
        ]);
        setCategories(catRes.data);
        setHeroSettings(heroRes.data?.value || {});
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const hero = heroSettings || {
    badge: 'Profesyonel Çözümler',
    title: 'Hizmetlerimiz',
    subtitle: 'Dört ana kategoride profesyonel hizmetler sunuyoruz. Her bir alanda uzman kadromuzla, işletmenizin ihtiyaçlarına özel çözümler geliştiriyoruz.',
    feature1_title: 'Güvenilir',
    feature1_subtitle: '%100 Memnuniyet',
    feature2_title: 'Hızlı',
    feature2_subtitle: 'Anında Çözüm'
  };

  const defaultCategories = [
    {
      id: '1',
      title: 'İnsan Kaynakları Hizmetleri',
      description: 'Şirketlerin sürdürülebilir büyümesi doğru insan kaynağıyla mümkündür. 30 yıllık sektör deneyimimizle, kurumların ihtiyaç duyduğu nitelikli çalışanları belirlemek, değerlendirmek ve doğru pozisyonlarla eşleştirmek için profesyonel insan kaynakları çözümleri sunuyoruz. Lojistik, üretim, sağlık ve hizmet sektörlerinde binlerce başarılı yerleştirme gerçekleştirdik.',
      icon: 'Users',
      images: [
        'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/8xuwsih2_ChatGPT%20Image%2017%20Mar%202026%2015_00_32.png',
        'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/w8mo2k90_ChatGPT%20Image%2017%20Mar%202026%2016_03_26.png'
      ],
      subcategories: [
        { 
          id: '1-1', 
          title: 'Mavi Yaka Personel Tedariki', 
          description: 'Kargo, lojistik ve depolama sektörlerinde vasıflı/vasıfsız personel desteği. Yükleme-boşaltma, elleçleme, paketleme ve ürün ayrıştırma operasyonları için günlük yevmiyeli veya daimi personel hizmeti sunuyoruz.',
          images: [
            'https://images.unsplash.com/photo-1573207535342-8c0f9506112e?w=800',
            'https://images.unsplash.com/photo-1768796372882-8b67936af681?w=800'
          ]
        },
        { 
          id: '1-2', 
          title: 'Seçme ve Yerleştirme', 
          description: 'Kurumsal firmaların işe alım süreçlerini, insan kaynakları planlamalarına paralel olarak yönetiyoruz. "Doğru işe, doğru iş gücü" anlayışıyla her personelin şirketin geleceğine katkı sağlamasını hedefliyoruz.',
          images: [
            'https://images.unsplash.com/photo-1758518730162-09a142505bfd?w=800',
            'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800'
          ]
        },
        { 
          id: '1-3', 
          title: 'Fabrika ve Üretim Personeli', 
          description: 'Üretim süreçlerinizin aksamaması için paketleme, ürün ayrıştırma, elleçleme ve genel üretim destek personeli hizmetlerini günlük yevmiyeli veya daimi kadrolarla sağlıyoruz.',
          images: [
            'https://images.unsplash.com/photo-1727517786578-ff2bb896b852?w=800',
            'https://images.pexels.com/photos/36423823/pexels-photo-36423823.jpeg?auto=compress&cs=tinysrgb&w=800'
          ]
        },
        { 
          id: '1-4', 
          title: 'Plaza ve İş Merkezi Personeli', 
          description: 'Plaza ve iş merkezleri için danışma personeli, teknik personel ve temizlik personeli hizmetleri sunuyoruz. Profesyonel ve temsil kabiliyeti yüksek personellerle hizmetinizdeyiz.',
          images: [
            'https://images.pexels.com/photos/5511130/pexels-photo-5511130.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/4269266/pexels-photo-4269266.jpeg?auto=compress&cs=tinysrgb&w=800'
          ]
        },
        { 
          id: '1-5', 
          title: 'Sağlık Kurumları Personeli', 
          description: 'Hastaneler ve sağlık kurumları için temizlik, danışma, hasta yönlendirme ve yardımcı hizmetler personeli tedarik ediyoruz. Hijyen ve hasta memnuniyeti odaklı eğitimli personellerimizle yanınızdayız.',
          images: [
            'https://images.unsplash.com/photo-1766069211780-ce28c0ba20d9?w=800',
            'https://images.unsplash.com/photo-1770663734902-0f2440bc665c?w=800'
          ]
        },
        { 
          id: '1-6', 
          title: 'Teknik Personel', 
          description: 'Reach Truck (RT) ve Forklift operatörü, teknik bakım personeli ve makine operatörleri tedariki. Sertifikalı ve deneyimli teknik kadro ile operasyonlarınızı destekliyoruz.',
          images: [
            'https://images.pexels.com/photos/4487446/pexels-photo-4487446.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/4487419/pexels-photo-4487419.jpeg?auto=compress&cs=tinysrgb&w=800'
          ]
        },
        { 
          id: '1-7', 
          title: 'Bordrolama Hizmetleri', 
          description: 'Personel maaş hesaplamaları, SGK bildirimleri, puantaj takibi ve yasal bordro işlemlerinin tamamını profesyonel ekibimizle yönetiyoruz. İş kanunu ve mevzuata uygun, hatasız bordro süreçleri için yanınızdayız.',
          images: [
            'https://images.pexels.com/photos/6863250/pexels-photo-6863250.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/7681091/pexels-photo-7681091.jpeg?auto=compress&cs=tinysrgb&w=800'
          ]
        },
      ]
    },
    {
      id: '2',
      title: 'Dijital Baskı Çözümleri',
      description: 'Markaların görünürlüğünü artıran tüm basılı materyaller için kaliteli ve hızlı dijital baskı hizmetleri sunuyoruz. Modern baskı teknolojileri ve uzman ekibimizle, kurumsal kimlik çalışmalarından promosyon ürünlerine, büyük format baskılardan özel tasarım ürünlere kadar geniş bir üretim kapasitesine sahibiz. Zamanında teslimat ve rekabetçi fiyatlarla müşteri memnuniyetini ön planda tutuyoruz.',
      icon: 'Printer',
      images: [
        'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/vi7ky692_Gemini_Generated_Image_9hbmth9hbmth9hbm.png',
        'https://images.pexels.com/photos/6620963/pexels-photo-6620963.jpeg?auto=compress&cs=tinysrgb&w=800'
      ],
      subcategories: [
        { id: '2-1', title: 'Kurumsal Kimlik Ürünleri', description: 'Kartvizit, antetli kağıt, zarf, dosya ve tüm kurumsal dokümanlarınız için profesyonel baskı çözümleri. Markanızı en iyi şekilde yansıtan kaliteli malzeme ve baskı teknikleri kullanıyoruz.' },
        { id: '2-2', title: 'Promosyon ve Tanıtım Ürünleri', description: 'Kalem, ajanda, takvim, USB, çanta ve özel promosyon materyalleri. Fuarlar, etkinlikler ve kurumsal hediyeler için markanızı öne çıkaran tasarım ve üretim hizmetleri.' },
        { id: '2-3', title: 'Afiş, Broşür ve Katalog', description: 'Dijital ve ofset baskı ile her boyutta afiş, broşür, katalog ve flyer üretimi. Yüksek çözünürlüklü baskı kalitesiyle dikkat çekici görsel materyaller hazırlıyoruz.' },
        { id: '2-4', title: 'Büyük Format Baskı', description: 'Billboard, totem, roll-up, branda ve araç giydirme için büyük format dijital baskı hizmetleri. Dış mekan dayanıklılığına sahip malzemelerle uzun ömürlü çözümler sunuyoruz.' },
        { id: '2-5', title: 'Etiket ve Ambalaj', description: 'Ürün etiketleri, ambalaj tasarımı ve baskısı. Gıda, kozmetik ve endüstriyel ürünler için kaliteli etiket ve ambalaj çözümleri.' },
      ]
    },
    {
      id: '3',
      title: 'Araç Kiralama Hizmetleri',
      description: 'Kurumsal ve bireysel ihtiyaçlara yönelik güvenilir araç kiralama hizmetleri sunuyoruz. Geniş araç filomuz, profesyonel sürücülerimiz ve esnek kiralama seçeneklerimizle her türlü ulaşım ihtiyacınızı karşılıyoruz. Binek araçlardan ticari araçlara, VIP transferlerden personel servislerine kadar kapsamlı çözümler sunuyoruz.',
      icon: 'Car',
      images: [
        'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/grf4qrup_Gemini_Generated_Image_ierh0nierh0nierh.png',
        'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800'
      ],
      subcategories: [
        { id: '3-1', title: 'Günlük ve Kısa Süreli Kiralama', description: 'Günlük, haftalık ve aylık kiralama seçenekleriyle esnek çözümler. İş seyahatleri, özel etkinlikler ve geçici ulaşım ihtiyaçları için ideal araç kiralama hizmeti.' },
        { id: '3-2', title: 'Uzun Dönem Filo Kiralama', description: 'Kurumsal firmalar için aylık ve yıllık filo kiralama çözümleri. Bakım, sigorta ve yönetim dahil tam kapsamlı operasyonel kiralama hizmetleri sunuyoruz.' },
        { id: '3-3', title: 'Şoförlü VIP Transfer', description: 'VIP ulaşım, havalimanı transferi ve özel etkinlik hizmetleri. Profesyonel ve tecrübeli sürücülerimizle konforlu ve güvenli yolculuk deneyimi.' },
        { id: '3-4', title: 'Personel Servisi', description: 'Kurumsal personel taşımacılığı için düzenli servis hizmetleri. Zamanında, güvenli ve konforlu personel ulaşımı çözümleri.' },
        { id: '3-5', title: 'Ticari Araç Kiralama', description: 'Minibüs, kamyonet ve panelvan kiralama hizmetleri. Lojistik, dağıtım ve kurumsal taşımacılık ihtiyaçlarınız için uygun filo seçenekleri.' },
      ]
    },
    {
      id: '4',
      title: 'Eğitim ve Danışmanlık',
      description: 'Şirketlerin büyüme süreçlerinde ihtiyaç duyduğu stratejik bilgi ve uygulama deneyimini eğitim ve danışmanlık programlarımızla sunuyoruz. Alanında uzman eğitmenlerimiz ve danışmanlarımızla, kurumların insan kaynağı kalitesini artırmak, süreçlerini iyileştirmek ve rekabet avantajı kazanmalarını sağlamak için kapsamlı programlar geliştiriyoruz.',
      icon: 'GraduationCap',
      images: [
        'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/gd9937yd_Gemini_Generated_Image_1jblzf1jblzf1jbl.png',
        'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800'
      ],
      subcategories: [
        { id: '4-1', title: 'Satış ve Pazarlama Eğitimleri', description: 'Satış tekniklerinden müşteri ilişkileri yönetimine, dijital pazarlamadan marka stratejilerine kadar kapsamlı satış ve pazarlama eğitim programları.' },
        { id: '4-2', title: 'Liderlik ve Yöneticilik', description: 'Orta ve üst düzey yöneticiler için liderlik becerileri, ekip yönetimi, karar alma ve stratejik düşünme eğitimleri.' },
        { id: '4-3', title: 'İş Sağlığı ve Güvenliği', description: 'İSG mevzuatı, risk değerlendirmesi ve güvenlik kültürü oluşturma konularında zorunlu ve gelişim eğitimleri.' },
        { id: '4-4', title: 'İnsan Kaynakları Danışmanlığı', description: 'İşe alım süreçleri, performans yönetimi, ücret sistemleri ve organizasyonel yapılandırma konularında danışmanlık hizmetleri.' },
        { id: '4-5', title: 'Operasyonel Verimlilik', description: 'Süreç iyileştirme, maliyet optimizasyonu ve kalite yönetimi konularında danışmanlık ve uygulama desteği.' },
      ]
    },
  ];

  // Subcategory image mappings for merging
  const subcategoryImageMap = {
    // İnsan Kaynakları Hizmetleri
    'Personel Tedariki': [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/5ckp734o_IMG-20260325-WA0039.jpg',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/2nswmgjr_IMG-20260325-WA0040.jpg'
    ],
    'Mavi Yaka Personel Tedariki': [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/5ckp734o_IMG-20260325-WA0039.jpg',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/2nswmgjr_IMG-20260325-WA0040.jpg'
    ],
    'Seçme ve Yerleştirme': [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/07ofoj27_1000200539.jpg',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/iikpjjwk_1000200533.jpg'
    ],
    'Fabrika Personeli': [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/qo9g2lfb_1000200542.jpg',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/zeg4i1dd_1000200541.jpg'
    ],
    'Fabrika ve Üretim Personeli': [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/qo9g2lfb_1000200542.jpg',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/zeg4i1dd_1000200541.jpg'
    ],
    'Plaza Personeli': [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/0fe8bmgx_1000200555.jpg',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/0y43hw7b_1000200556.jpg'
    ],
    'Plaza ve İş Merkezi Personeli': [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/0fe8bmgx_1000200555.jpg',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/0y43hw7b_1000200556.jpg'
    ],
    'Sağlık Personeli': [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/hg5wwoma_1000200591.jpg',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/i9b441gq_1000200592.jpg'
    ],
    'Sağlık Kurumları Personeli': [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/hg5wwoma_1000200591.jpg',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/i9b441gq_1000200592.jpg'
    ],
    'Teknik Personel': [
      'https://images.pexels.com/photos/4487446/pexels-photo-4487446.jpeg?auto=compress&cs=tinysrgb&w=800',
      'https://images.pexels.com/photos/4487419/pexels-photo-4487419.jpeg?auto=compress&cs=tinysrgb&w=800'
    ],
    // Dijital Baskı Çözümleri
    'Kurumsal Kimlik': [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/yxzo4x4m_WhatsApp%20Image%202026-03-26%20at%2013.04.20.jpeg',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/aqyta05v_WhatsApp%20Image%202026-03-26%20at%2013.04.21.jpeg'
    ],
    'Promosyon Ürünleri': [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/jihu1vf1_WhatsApp%20Image%202026-03-26%20at%2013.06.21%20%281%29.jpeg',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/tiqx5dmu_WhatsApp%20Image%202026-03-26%20at%2013.06.21.jpeg'
    ],
    'Afiş ve Broşür': [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/7zlubxxt_WhatsApp%20Image%202026-03-26%20at%2009.53.40%20%281%29.jpeg',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/1pic7hla_WhatsApp%20Image%202026-03-26%20at%2009.53.40.jpeg'
    ],
    // Araç Kiralama Hizmetleri
    'Günlük Kiralama': [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/pha94wbv_4.jpg.jpeg',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/kt7v4n5x_5.jpg.jpeg'
    ],
    'Uzun Dönem Kiralama': [
      'https://images.unsplash.com/photo-1763180785425-46ea06a0fc50?w=800',
      'https://images.pexels.com/photos/36729874/pexels-photo-36729874.jpeg?auto=compress&cs=tinysrgb&w=800'
    ],
    'Şoförlü Araç': [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/08on8tar_1.jpg.jpeg',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/kk2xtvmv_6.jpg.jpeg'
    ],
    // Eğitim ve Danışmanlık
    'Satış Eğitimleri': [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/2uvja40v_WhatsApp%20Image%202026-03-26%20at%2010.23.52%20%281%29.jpeg',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/yzcp550h_WhatsApp%20Image%202026-03-26%20at%2010.23.52.jpeg'
    ],
    'Satış ve Pazarlama Eğitimleri': [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/2uvja40v_WhatsApp%20Image%202026-03-26%20at%2010.23.52%20%281%29.jpeg',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/yzcp550h_WhatsApp%20Image%202026-03-26%20at%2010.23.52.jpeg'
    ],
    'Pazarlama Danışmanlığı': [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/unuqg66b_WhatsApp%20Image%202026-03-26%20at%2012.59.06%20%281%29.jpeg',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/dpnvbt2v_WhatsApp%20Image%202026-03-26%20at%2012.59.06.jpeg'
    ],
    'Kurumsal Gelişim': [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/4sz1a3jx_WhatsApp%20Image%202026-03-26%20at%2013.09.57%20%281%29.jpeg',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/r0zntvtl_WhatsApp%20Image%202026-03-26%20at%2013.09.57.jpeg'
    ],
    'Liderlik ve Yöneticilik': [
      'https://images.unsplash.com/photo-1765438864227-288900d09d26?w=800',
      'https://images.unsplash.com/photo-1758691736084-4ef3e6f6a2cf?w=800'
    ],
    'İş Sağlığı ve Güvenliği': [
      'https://images.pexels.com/photos/8961251/pexels-photo-8961251.jpeg?auto=compress&cs=tinysrgb&w=800',
      'https://images.pexels.com/photos/8961065/pexels-photo-8961065.jpeg?auto=compress&cs=tinysrgb&w=800'
    ],
    'İnsan Kaynakları Danışmanlığı': [
      'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=800',
      'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=800'
    ],
    'Operasyonel Verimlilik': [
      'https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg?auto=compress&cs=tinysrgb&w=800',
      'https://images.pexels.com/photos/6476808/pexels-photo-6476808.jpeg?auto=compress&cs=tinysrgb&w=800'
    ]
  };

  // Merge database categories with default images for subcategories
  const mergeWithDefaultImages = (dbCategories) => {
    return dbCategories.map(category => {
      const defaultCategory = defaultCategories.find(dc => dc.id === category.id || dc.title === category.title);
      if (category.subcategories) {
        const mergedSubcategories = category.subcategories.map(sub => {
          // Try to find images from the mapping
          const images = sub.images || subcategoryImageMap[sub.title] || [];
          return {
            ...sub,
            images: images
          };
        });
        return {
          ...category,
          images: category.images || defaultCategory?.images || [],
          subcategories: mergedSubcategories
        };
      }
      return {
        ...category,
        images: category.images || defaultCategory?.images || []
      };
    });
  };

  const displayCategories = categories.length > 0 ? mergeWithDefaultImages(categories) : defaultCategories;

  return (
    <Layout>
      {/* Hero Header */}
      <section className="relative bg-[#005CB2] py-12 md:py-24 overflow-hidden" data-testid="services-header">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-48 md:w-96 h-48 md:h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-32 md:w-64 h-32 md:h-64 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        </div>
        
        <div className="eta-container relative z-10 px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-white/10 rounded-full mb-4 md:mb-6">
                <Briefcase className="w-3.5 h-3.5 md:w-4 md:h-4 text-white" />
                <span className="text-xs md:text-sm font-medium text-white">
                  {hero.badge}
                </span>
              </div>
              
              <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {hero.title}
              </h1>
              
              <p className="text-sm md:text-lg text-white/80 leading-relaxed mb-6 md:mb-8">
                {hero.subtitle}
              </p>

              {/* Features */}
              <div className="grid grid-cols-2 gap-2 md:gap-4">
                <div className="flex items-center gap-2 md:gap-3 bg-white/10 rounded-lg p-2.5 md:p-4">
                  <Shield className="w-6 h-6 md:w-8 md:h-8 text-white flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-xs md:text-base truncate">{hero.feature1_title}</p>
                    <p className="text-white/60 text-xs md:text-sm truncate">{hero.feature1_subtitle}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 md:gap-3 bg-white/10 rounded-lg p-2.5 md:p-4">
                  <Clock className="w-6 h-6 md:w-8 md:h-8 text-white flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-xs md:text-base truncate">{hero.feature2_title}</p>
                    <p className="text-white/60 text-xs md:text-sm truncate">{hero.feature2_subtitle}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Service Icons Grid */}
            <div className="hidden lg:grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur rounded-2xl p-6 text-center hover:bg-white/20 transition-colors h-40 flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <p className="text-white font-semibold">İnsan Kaynakları</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-2xl p-6 text-center hover:bg-white/20 transition-colors h-40 flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <Printer className="w-8 h-8 text-white" />
                </div>
                <p className="text-white font-semibold">Dijital Baskı</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-2xl p-6 text-center hover:bg-white/20 transition-colors h-40 flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <Car className="w-8 h-8 text-white" />
                </div>
                <p className="text-white font-semibold">Araç Kiralama</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-2xl p-6 text-center hover:bg-white/20 transition-colors h-40 flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <GraduationCap className="w-8 h-8 text-white" />
                </div>
                <p className="text-white font-semibold">Eğitim</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-8 md:py-16 bg-white" data-testid="services-list">
        <div className="eta-container px-4 md:px-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#005CB2] animate-spin" />
            </div>
          ) : (
            <div className="space-y-6 md:space-y-8">
              {displayCategories.map((category, index) => {
                const IconComponent = iconMap[category.icon] || Users;
                return (
                  <Card 
                    key={category.id} 
                    data-testid={`service-detail-${index}`}
                    className="overflow-hidden border-slate-200"
                  >
                    <div className="flex flex-col lg:grid lg:grid-cols-3">
                      {/* Single Image */}
                      <div className="lg:col-span-1 aspect-[4/3] md:aspect-video lg:aspect-auto relative min-h-[200px] md:min-h-[280px]">
                        <img 
                          src={category.images?.[0] || category.image_url} 
                          alt={category.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent lg:hidden pointer-events-none" />
                        <div className="absolute bottom-3 left-3 right-3 lg:hidden pointer-events-none">
                          <div className="flex items-center gap-2">
                            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <IconComponent className="w-5 h-5 text-[#005CB2]" />
                            </div>
                            <h2 className="text-lg font-bold text-white line-clamp-2">
                              {category.title}
                            </h2>
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="lg:col-span-2 p-4 md:p-6 lg:p-8">
                        <div className="hidden lg:flex items-center gap-4 mb-6">
                          <div className="w-14 h-14 bg-[#005CB2]/10 rounded-xl flex items-center justify-center">
                            <IconComponent className="w-7 h-7 text-[#005CB2]" />
                          </div>
                          <h2 className="eta-heading-3">
                            {category.title}
                          </h2>
                        </div>

                        <p className="text-sm md:text-base text-slate-600 leading-relaxed mb-4 md:mb-6">
                          {category.description}
                        </p>

                        {/* Subcategories */}
                        {category.subcategories && category.subcategories.length > 0 && (
                          <div>
                            <h3 className="text-xs md:text-sm font-bold text-[#005CB2] mb-3 md:mb-4 tracking-wider">
                              ALT HİZMETLER
                            </h3>
                            <Accordion type="multiple" className="space-y-1.5 md:space-y-2">
                              {category.subcategories.map((sub, subIndex) => (
                                <AccordionItem 
                                  key={sub.id} 
                                  value={sub.id}
                                  className="border border-slate-200 rounded-lg px-3 md:px-4"
                                >
                                  <AccordionTrigger 
                                    data-testid={`subcategory-trigger-${index}-${subIndex}`}
                                    className="hover:no-underline py-3 md:py-4"
                                  >
                                    <div className="flex items-center gap-2 md:gap-3">
                                      <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-[#3D94F6] flex-shrink-0" />
                                      <span className="text-xs md:text-sm font-medium text-slate-800 text-left">
                                        {sub.title}
                                      </span>
                                    </div>
                                  </AccordionTrigger>
                                  <AccordionContent className="pb-3 md:pb-4">
                                    {/* Sub-service with optional image carousel */}
                                    <div className="pl-6 md:pl-8">
                                      {sub.images && sub.images.length > 0 && (
                                        <div className="mb-3 md:mb-4 rounded-lg overflow-hidden aspect-video relative">
                                          <ImageCarousel images={sub.images} title={sub.title} />
                                        </div>
                                      )}
                                      <p className="text-xs md:text-sm text-slate-600 leading-relaxed">
                                        {sub.description}
                                      </p>
                                    </div>
                                  </AccordionContent>
                                </AccordionItem>
                              ))}
                            </Accordion>
                            
                            {/* Detail Page Link */}
                            <div className="mt-4 pt-4 border-t border-slate-200">
                              <Link to={`/hizmetler/${slugMap[category.title] || ''}`}>
                                <Button 
                                  variant="outline" 
                                  className="w-full border-[#005CB2] text-[#005CB2] hover:bg-[#005CB2] hover:text-white gap-2"
                                >
                                  Detaylı Bilgi
                                  <ArrowRight className="w-4 h-4" />
                                </Button>
                              </Link>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="py-8 md:py-16 bg-slate-50" data-testid="additional-info">
        <div className="eta-container px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
            <Card className="text-center p-4 md:p-8 border-slate-200">
              <CardHeader className="pb-2 md:pb-4">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-[#005CB2]/10 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <Users className="w-6 h-6 md:w-8 md:h-8 text-[#005CB2]" />
                </div>
                <CardTitle className="text-base md:text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Uzman Kadro
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <p className="text-sm md:text-base text-slate-600">
                  Alanında deneyimli profesyonellerden oluşan ekibimizle hizmetinizdeyiz.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-4 md:p-8 border-slate-200">
              <CardHeader className="pb-2 md:pb-4">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-[#005CB2]/10 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <CheckCircle2 className="w-6 h-6 md:w-8 md:h-8 text-[#005CB2]" />
                </div>
                <CardTitle className="text-base md:text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Kalite Garantisi
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <p className="text-sm md:text-base text-slate-600">
                  Tüm hizmetlerimizde yüksek kalite standartlarını garanti ediyoruz.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-4 md:p-8 border-slate-200">
              <CardHeader className="pb-2 md:pb-4">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-[#005CB2]/10 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <GraduationCap className="w-6 h-6 md:w-8 md:h-8 text-[#005CB2]" />
                </div>
                <CardTitle className="text-base md:text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Sürekli Gelişim
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <p className="text-sm md:text-base text-slate-600">
                  Sektördeki gelişmeleri takip ederek hizmet kalitemizi sürekli artırıyoruz.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ServicesPage;
